// 一些全局配置储存在这里，如i18n, sso等
import { Commit } from 'vuex'
import {
  UserInfo,
  IFooterConfigItem,
  IEventInfo,
  ISeoItem,
  SiteLanguageItem,
  IDictItem,
  LanguageItem,
  CountryItem,
  ISupplyInfoItem
} from '@/interface'
import { TENANT_ID_MAP } from '@/utils'

const state = {
  messages: {} as {
    en: Record<string, any>
  },
  seoConfig: [] as ISeoItem[],
  defaultPageTitle: '',
  siteInfo: {} as { siteCode: string, siteId: number },
  footerConfig: [] as IFooterConfigItem[],
  siteLanguageConfig: [] as SiteLanguageItem[],
  userInfo: {} as UserInfo,
  // 事件相关状态
  event: { enableScroll: true, showHeader: true } as IEventInfo,
  isoCode: '',
  dataLoaded: false, // 标记数据是否初始化，防止每次页面跳转就重新请求数据,
  isArSite: false,
  isEU: false, // 是否是eu站
  isAsia: false, // 是否是asia站
  orderFoodDisplay: false,
  tenantId: '',
  currentLangKey: null as null | number, // 目前页面里面用的语言id
  appLoading: true,
  // 该租户的认证大类
  certificationTypes: [] as Array<{ kindName: string, kindType: number | string }>,
  dictItemList: [] as IDictItem[],
  showNonCertiMenu: true,
  languageList: [] as LanguageItem[],
  countryList: [] as CountryItem[],
  showPreferences: false,
  gatherInfoVisible: false,
  supplyInfo: [] as ISupplyInfoItem[],
  microEnrollCourse: false // 微影报名课程
}

const globalStore = {
  namespaced: true,
  state,
  getters: {
    isLogin: (state: GlobalState): boolean => !!state.userInfo,
    getEvent: (state: GlobalState): IEventInfo => state.event,
    socialLinks: (state: GlobalState) => {
      const mediaLinks = state.footerConfig.filter(i => i.areaKind === '7')
      return mediaLinks
    },
    shouldUserSupplyInfo: (state: GlobalState) => {
      if (state.supplyInfo.length > 0) {
        const keyList = Object.keys(state.supplyInfo[0]) as Array<keyof ISupplyInfoItem>
        const necessaryKeys = keyList.filter((item: keyof ISupplyInfoItem) => state.supplyInfo[0][item] === '1')
        return !necessaryKeys.every((item: keyof ISupplyInfoItem) => !!state.userInfo[item])
      }
      return false
    },
    ifSiteLanguageOff (state: GlobalState) {
      return state.languageList.length <= 1
    }
  },
  mutations: {
    setI18n (state: GlobalState, messages: GlobalState['messages']): void {
      state.messages = messages
    },
    setSeoConfig (state: GlobalState, config: GlobalState['seoConfig']): void {
      state.seoConfig = config
    },
    setFooterConfig (state: GlobalState, config: GlobalState['footerConfig']): void {
      state.footerConfig = config
    },
    setSiteInfo (state: GlobalState, config: GlobalState['siteInfo']): void {
      state.siteInfo = config
    },
    setUserInfo (state: GlobalState, userInfo: GlobalState['userInfo']) {
      if (userInfo?.languageId) {
        userInfo.languageId = Number(userInfo.languageId)
      }
      state.userInfo = userInfo
    },
    setEvent (state: GlobalState, event: GlobalState['event']) {
      state.event = event
    },
    setIsoCode (state: GlobalState, code: GlobalState['isoCode']) {
      state.isoCode = code
    },
    setDataLoaded (state: GlobalState, status: boolean) {
      state.dataLoaded = status
    },
    setTenantId (state: GlobalState, id: string) {
      state.tenantId = id
    },
    setIsArSite (state: GlobalState, bool: boolean) {
      state.isArSite = bool
    },
    setSiteLanguageConfig (state: GlobalState, config: GlobalState['siteLanguageConfig']): void {
      state.siteLanguageConfig = config
    },
    setIsEU (state: GlobalState, status: boolean) {
      state.isEU = status
    },
    setIsAsia (state: GlobalState, status: boolean) {
      state.isAsia = status
    },
    setCertificationTypes (state: GlobalState, types: GlobalState['certificationTypes']) {
      state.certificationTypes = types
    },
    setDefaultTitle (state: GlobalState, tenantId: string) {
      const TITLE_MAP = {
        [TENANT_ID_MAP.hikvision]: 'Hikvision eLearning',
        [TENANT_ID_MAP.micro]: 'HIKMICRO eLearning'
      }
      state.defaultPageTitle = TITLE_MAP[tenantId]
    },
    setDictItemList (state: GlobalState, list: IDictItem[]) {
      state.dictItemList = list
    },
    setLanguageList (state: GlobalState, list: LanguageItem[]) {
      if (Array.isArray(list)) {
        const l = list.filter(Boolean)
        state.languageList = [...l, ...state.languageList]
      }
    },
    setCountryList (state: GlobalState, list: CountryItem[]) {
      const l = list.filter(Boolean)
      state.countryList = [...l, ...state.countryList]
    },
    setLangugeKey (state: GlobalState, key: number) {
      state.currentLangKey = key
    },
    setAppLoading (state: GlobalState, loading: boolean) {
      state.appLoading = loading
    },
    setShowNonCertiMenu (state: GlobalState, visible: boolean) {
      state.showNonCertiMenu = visible
    },
    setOrderFoodDisplay (state: GlobalState, visible: boolean) {
      state.orderFoodDisplay = visible
    },
    setShowPreferences (state: GlobalState, visible: boolean) {
      state.showPreferences = visible
    },
    setGatherInfoVisible (state: GlobalState, visible: boolean) {
      state.gatherInfoVisible = visible
    },
    setSupplyInfo (state: GlobalState, list: ISupplyInfoItem[]) {
      state.supplyInfo = list
    },
    setMicroEnrollCourse (state: GlobalState, visible: boolean) {
      state.microEnrollCourse = visible
    }
  },
  actions: {
    setI18n ({ commit }: { commit: Commit }, { messages }: { messages: GlobalState['messages'] }): void {
      commit('setI18n', messages)
    },
    setSeoConfig ({ commit }: { commit: Commit }, { config }: { config: GlobalState['seoConfig'] }): void {
      commit('setSeoConfig', config)
    },
    setFooterConfig ({ commit }: { commit: Commit }, { footerConfig }: { footerConfig: GlobalState['footerConfig'] }): void {
      commit('setFooterConfig', footerConfig)
    },
    setEvent ({ commit }: { commit: Commit }, { event }: { event: GlobalState['event'] }): void {
      commit('setEvent', event)
    },
    setSiteLanguageConfig ({ commit }: { commit: Commit }, { siteLanguageConfig }: { siteLanguageConfig: GlobalState['siteLanguageConfig'] }): void {
      commit('setSiteLanguageConfig', siteLanguageConfig)
    }
  }
}

export type GlobalState = typeof state

export {
  globalStore
}
